/**
 * DeliverySlotsSheet — modale agence de planification de livraison.
 * ──────────────────────────────────────────────────────────────────
 * Deux modes internes accessibles via pill :
 *   • « Proposer des créneaux » : 2 à 5 créneaux → envoi au client
 *   • « Fixer directement »    : 1 date + 1 heure → confirmation immédiate
 *
 * Aussi : bouton discret « Annuler la programmation » quand un rendez-vous
 * ou des propositions existent.
 *
 * Design strictement wyvio-ui.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Plus, Trash2, Send, CheckCircle2, AlertCircle, XCircle, Truck, Zap,
} from "lucide-react";
import { Button, SegmentedControl, MicroLabel, Chip } from "@/components/wyvio-ui";
import api from "@/lib/api";

const emptySlot = () => ({
  key: Math.random().toString(36).slice(2),
  date: "",
  time: "",
});

const DeliverySlotsSheet = ({
  dossierId,
  current,          // objet delivery courant depuis /api/dossiers/{id}/delivery
  defaultLocation,  // adresse agence pré-remplie
  onClose,
  onSaved,
}) => {
  const [tab, setTab] = useState(() => (current?.status === "confirmed" ? "direct" : "propose"));
  const [slots, setSlots] = useState(() => {
    if (current?.status === "proposed" && current.proposals?.length) {
      return current.proposals.map((p) => ({
        key: p.id,
        date: p.date,
        time: p.time,
      }));
    }
    return [emptySlot(), emptySlot(), emptySlot()];
  });
  const [directDate, setDirectDate] = useState(
    current?.proposals?.[0]?.date || "",
  );
  const [directTime, setDirectTime] = useState(
    current?.proposals?.[0]?.time || "",
  );
  const [location, setLocation] = useState(
    current?.location || defaultLocation || "",
  );
  const [notes, setNotes] = useState(current?.notes || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const validSlots = useMemo(
    () => slots.filter((s) => s.date && s.time),
    [slots],
  );

  const updateSlot = (i, patch) =>
    setSlots((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const addSlot = () =>
    setSlots((prev) => (prev.length < 5 ? [...prev, emptySlot()] : prev));

  const removeSlot = (i) =>
    setSlots((prev) => (prev.length > 2 ? prev.filter((_, idx) => idx !== i) : prev));

  const submitPropose = async () => {
    if (validSlots.length < 2) return setError("Merci d'ajouter au moins 2 créneaux");
    setBusy(true); setError("");
    try {
      await api.proposeDelivery(dossierId, {
        slots: validSlots.map((s) => ({ date: s.date, time: s.time })),
        location: location || null,
        notes: notes || null,
      });
      onSaved?.();
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const submitDirect = async () => {
    if (!directDate || !directTime) return setError("Date et heure sont requises");
    setBusy(true); setError("");
    try {
      await api.scheduleDelivery(dossierId, {
        date: directDate, time: directTime,
        location: location || null,
        notes: notes || null,
      });
      onSaved?.();
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    if (!confirm("Annuler le rendez-vous et effacer les propositions ?")) return;
    setBusy(true); setError("");
    try {
      await api.cancelDelivery(dossierId);
      onSaved?.();
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const hasActive = current && current.status !== "none" && current.status !== "canceled";

  return createPortal(
    <div
      data-testid="delivery-slots-sheet"
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <div onClick={onClose} className="absolute inset-0 bg-[#111111]/25 backdrop-blur-[2px]" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-[520px] bg-white dark:bg-[#111111] rounded-t-3xl sm:rounded-3xl border border-[#EEF2F5] dark:border-[#262626] p-6 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <div className="flex items-center gap-2">
              <Truck size={16} strokeWidth={2.25} className="text-[#0A0A0A] dark:text-white" />
              <h3 className="text-[19px] font-semibold tracking-[-0.02em] text-[#0A0A0A] dark:text-white">
                Planifier la livraison
              </h3>
            </div>
            <p className="mt-1 text-[12.5px] text-[#6B7280] dark:text-[#A3A3A3]">
              {hasActive
                ? current.status === "proposed"
                  ? `${current.proposals?.length || 0} créneaux envoyés au client`
                  : "Rendez-vous confirmé"
                : "Proposez 2 à 3 créneaux au client, il choisira en un tap."}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            data-testid="delivery-slots-close"
            className="h-8 w-8 rounded-full flex items-center justify-center text-[#6B7280] dark:text-[#A3A3A3] hover:text-[#0A0A0A] dark:hover:text-white hover:bg-[#F7F8FA] dark:hover:bg-[#1A1A1A]"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <div className="mt-4">
          <SegmentedControl
            testId="delivery-slots-mode"
            value={tab}
            onChange={setTab}
            options={[
              { key: "propose", label: "Proposer" },
              { key: "direct",  label: "Fixer direct" },
            ]}
          />
        </div>

        {tab === "propose" ? (
          <div className="mt-5 space-y-3">
            {slots.map((s, i) => (
              <div
                key={s.key}
                data-testid={`delivery-slot-row-${i}`}
                className="flex items-center gap-2"
              >
                <input
                  type="date"
                  value={s.date}
                  onChange={(e) => updateSlot(i, { date: e.target.value })}
                  data-testid={`delivery-slot-date-${i}`}
                  className="flex-1 h-11 rounded-xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-3 text-[13.5px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white"
                />
                <input
                  type="text"
                  value={s.time}
                  onChange={(e) => updateSlot(i, { time: e.target.value })}
                  placeholder="14h00"
                  data-testid={`delivery-slot-time-${i}`}
                  className="w-20 h-11 rounded-xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-3 text-[13.5px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white text-center tabular-nums"
                />
                <button
                  onClick={() => removeSlot(i)}
                  disabled={slots.length <= 2}
                  data-testid={`delivery-slot-remove-${i}`}
                  aria-label="Supprimer ce créneau"
                  className="h-11 w-11 rounded-xl border border-[#EEF2F5] dark:border-[#262626] flex items-center justify-center text-[#6B7280] hover:text-[#EF4444] disabled:opacity-40"
                >
                  <Trash2 size={14} strokeWidth={2} />
                </button>
              </div>
            ))}

            <button
              onClick={addSlot}
              disabled={slots.length >= 5}
              data-testid="delivery-slot-add"
              className="w-full h-11 rounded-xl border border-dashed border-[#EEF2F5] dark:border-[#262626] flex items-center justify-center gap-2 text-[12.5px] font-medium text-[#6B7280] dark:text-[#A3A3A3] hover:text-[#0A0A0A] dark:hover:text-white hover:border-[#DCE1E7] disabled:opacity-40"
            >
              <Plus size={14} strokeWidth={2.5} />
              Ajouter un créneau
            </button>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            <div className="flex gap-2">
              <input
                type="date"
                value={directDate}
                onChange={(e) => setDirectDate(e.target.value)}
                data-testid="delivery-direct-date"
                className="flex-1 h-11 rounded-xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-3 text-[13.5px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white"
              />
              <input
                type="text"
                value={directTime}
                onChange={(e) => setDirectTime(e.target.value)}
                placeholder="14h00"
                data-testid="delivery-direct-time"
                className="w-24 h-11 rounded-xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-3 text-[13.5px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white text-center tabular-nums"
              />
            </div>
            <p className="text-[11.5px] text-[#6B7280] dark:text-[#A3A3A3]">
              Utilisez cette option quand la date est déjà convenue oralement avec le client.
            </p>
          </div>
        )}

        {/* Lieu + notes — communs aux deux modes */}
        <div className="mt-5 space-y-3">
          <label className="block">
            <MicroLabel>Lieu du rendez-vous</MicroLabel>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              data-testid="delivery-location"
              placeholder="Adresse de livraison"
              className="mt-2 w-full h-11 rounded-xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-4 text-[13.5px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white"
            />
          </label>
          <label className="block">
            <MicroLabel>Note interne (facultatif)</MicroLabel>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="delivery-notes"
              className="mt-2 w-full rounded-xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-4 py-3 text-[13.5px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white resize-none"
            />
          </label>
        </div>

        {error && (
          <div
            data-testid="delivery-error"
            className="mt-4 rounded-xl bg-[#FEE2E2] dark:bg-[#3B0F0F] text-[#991B1B] dark:text-[#FCA5A5] text-[12.5px] px-3 py-2 flex items-center gap-2"
          >
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {tab === "propose" ? (
            <Button
              variant="primary"
              size="lg"
              onClick={submitPropose}
              disabled={busy}
              loading={busy}
              icon={Send}
              data-testid="delivery-submit-propose"
              className="flex-1 justify-center h-11"
            >
              Envoyer au client
            </Button>
          ) : (
            <Button
              variant="primary"
              size="lg"
              onClick={submitDirect}
              disabled={busy}
              loading={busy}
              icon={Zap}
              data-testid="delivery-submit-direct"
              className="flex-1 justify-center h-11"
            >
              Fixer le rendez-vous
            </Button>
          )}
          {hasActive && (
            <button
              onClick={submitCancel}
              data-testid="delivery-cancel"
              disabled={busy}
              className="h-11 px-4 rounded-full text-[13px] font-semibold text-[#EF4444] hover:bg-[#FEE2E2] dark:hover:bg-[#3B0F0F] inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <XCircle size={14} strokeWidth={2} />
              Annuler
            </button>
          )}
        </div>

        {current?.status === "proposed" && (
          <div className="mt-4 flex items-center gap-2 text-[11.5px] text-[#6B7280] dark:text-[#A3A3A3]">
            <Chip size="sm" tone="accent">
              <CheckCircle2 size={10} strokeWidth={2.5} />
              Envoyés
            </Chip>
            En attente du choix du client.
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default DeliverySlotsSheet;
